import { Card, Grid } from '@mui/material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { createRef, useEffect, useState, useContext, useRef, useMemo } from 'react';
import Draggable, { type DraggableData, type DraggableEvent, type DraggableEventHandler } from 'react-draggable';
import BackArrow from 'components/back-arrow/BackArrow';

import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import { AuthContext, ViewContext, TextInput } from 'components/lib';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Menu from '@mui/material/Menu';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import {
  DeleteIcon,
  CopyIcon,
  EditIcon,
  VerticalDotsIcon,
  ExpandIcon,
  FullScreenIcon,
  RefreshIcon,
  LeftDropDownArrow,
  RightDropDownArrow,
} from 'components/icons';
import EditableLabel from '../components/EditableLabel';
import {
  copyProjectObservation,
  deleteProjectObservation,
  getProjectObservations,
  getProjectReports,
  reorderProjectObservations,
  replaceProjectObservationImage,
  updateProjectObservation,
} from '../../../api/projectApi';
import {
  AUTO_SCROLL_MAX_SPEED_PX,
  AUTO_SCROLL_THRESHOLD_PX,
  DRAG_MIN_DISTANCE,
  applyCustomOrderToRecords,
  buildProjectObservationImageUrl,
  buildProjectObservationStateUrl,
  buildProjectObservationThumbnailUrl,
  getCurrentOrder,
  getMultiSelectModifierLabel,
  getPointerClientX,
  getPointerClientY,
  isDeletedObservation,
  isMultiSelectModifierPressed,
  orderObservationRecords,
  reorderIdsGroup,
  type DragState,
  type ObservationId,
  type ObservationRecord,
  type ObservationReport,
  type PointerPosition,
  type SlotPosition,
} from './recordUtils';

const Records = () => {
  const context = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const currentUserId = authContext.user?.id;
  const showDeletedItems =
    ['dev', 'support'].includes(authContext.user?.appRole ?? '') &&
    Boolean(currentUserId && localStorage.getItem(`dashboard.showDeletedProjects.${currentUserId}`) === 'true');
  const [records, setRecords] = useState<ObservationRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [visualOrder, setVisualOrder] = useState<string[] | null>(null);
  const [isDropCommitting, setIsDropCommitting] = useState(false);

  const [reports, setReports] = useState<ObservationReport[]>([]);
  const [willBeUpdatedRecord, setWillBeUpdatedRecord] = useState<ObservationRecord | null>(null);
  const [sortBy, setSortBy] = useState<'custom' | 'date'>('custom');
  const isCustomSort = sortBy === 'custom';

  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const reportId = queryParams.get('report');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ObservationRecord | null>(null);
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [previewImageMaxHeight, setPreviewImageMaxHeight] = useState<number | null>(null);
  const [previewImageInsets, setPreviewImageInsets] = useState<{ left: number; right: number } | null>(null);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const multiSelectModifierLabel = useMemo(() => getMultiSelectModifierLabel(), []);
  const [isReplacingImage, setIsReplacingImage] = useState(false);
  const selectedReport = reports?.find((report) => String(report?.id) === String(reportId));
  const selectedReportGridId = selectedReport?.gridId ?? selectedReport?.grid_id;

  const getReports = async () => {
    try {
      const data = await getProjectReports(String(projectId));
      setReports(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    getReports();
  }, [projectId]);

  const selectedRecordDescription = selectedRecord?.description?.trim() ?? '';
  const recordNodeRefs = useRef<Record<string, React.RefObject<HTMLDivElement | null>>>({});
  const suppressNextPreviewRef = useRef(false);
  const dragDistanceRef = useRef(0);
  const dragStartOrderRef = useRef<string[]>([]);
  const dragSlotPositionsRef = useRef<(SlotPosition | null)[]>([]);
  const dragPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const activeDragRecordIdsRef = useRef<string[]>([]);
  const pageContainerRef = useRef<HTMLDivElement | null>(null);
  const previewModalRef = useRef<HTMLDivElement | null>(null);
  const previewImageWrapRef = useRef<HTMLDivElement | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const previewActionsRef = useRef<HTMLDivElement | null>(null);
  const previewEnteredFullscreenRef = useRef(false);
  const pointerPositionRef = useRef<PointerPosition | null>(null);
  const replaceImageInputRef = useRef<HTMLInputElement | null>(null);
  const pendingReplaceRecordRef = useRef<ObservationRecord | null>(null);
  const autoScrollStateRef = useRef<{ frame: number | null; draggedId: string | null }>({
    frame: null,
    draggedId: null,
  });

  const getRecordNodeRef = (recordId: ObservationId) => {
    if (!recordNodeRefs.current[recordId]) {
      recordNodeRefs.current[recordId] = createRef();
    }
    return recordNodeRefs.current[recordId];
  };

  const enrichRecordsWithAssets = async (items: ObservationRecord[]) =>
    Promise.all(
      items.map(async (record) => {
        const imageUrl = await getImageUrl(record.id, record.imageId);
        const thumbnailUrl = await getThumbnailUrl(record.id, record.imageId);
        const stateUrl = await getStateUrl(record.id);
        return {
          ...record,
          imageUrl,
          thumbnailUrl,
          stateUrl,
        };
      })
    );

  const sortedRecords = useMemo(
    () =>
      orderObservationRecords(
        records.filter((record) => showDeletedItems || !isDeletedObservation(record)),
        sortBy
      ),
    [records, showDeletedItems, sortBy]
  );
  const reorderableRecords = useMemo(
    () => sortedRecords.filter((record) => !isDeletedObservation(record)),
    [sortedRecords]
  );

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as 'custom' | 'date');
  };

  const getImageUrl = async (recordId: ObservationId, imageId?: ObservationId) => {
    const url = buildProjectObservationImageUrl(String(projectId), String(reportId), String(recordId));
    return imageId ? `${url}?image=${encodeURIComponent(String(imageId))}` : url;
  };

  const getStateUrl = async (recordId: ObservationId) => {
    return buildProjectObservationStateUrl(String(projectId), String(reportId), String(recordId));
  };

  const getThumbnailUrl = async (recordId: ObservationId, imageId?: ObservationId) => {
    const url = buildProjectObservationThumbnailUrl(String(projectId), String(reportId), String(recordId));
    return imageId ? `${url}?image=${encodeURIComponent(String(imageId))}` : url;
  };

  const getStateData = async (stateUrl: string | undefined) => {
    if (!stateUrl) {
      return null;
    }
    try {
      const response = await fetch(stateUrl, { credentials: 'include' });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching state data:', error);
      return null;
    }
  };

  const handlePreviewRecord = (record: ObservationRecord | null) => {
    setSelectedRecord(record);
    handleOpenPreviewModal();
  };

  const requestPreviewFullscreen = async () => {
    if (typeof document === 'undefined' || document.fullscreenElement) {
      return;
    }

    const fullscreenTarget = document.documentElement;
    if (!fullscreenTarget.requestFullscreen) {
      return;
    }

    try {
      await fullscreenTarget.requestFullscreen();
      previewEnteredFullscreenRef.current = true;
      setIsPreviewFullscreen(true);
    } catch (error) {
      previewEnteredFullscreenRef.current = false;
      setIsPreviewFullscreen(false);
      console.debug('Preview fullscreen request was rejected.', error);
    }
  };

  const exitPreviewFullscreen = async () => {
    if (typeof document === 'undefined' || !previewEnteredFullscreenRef.current || !document.fullscreenElement) {
      previewEnteredFullscreenRef.current = false;
      setIsPreviewFullscreen(false);
      return;
    }

    try {
      await document.exitFullscreen();
    } catch (error) {
      console.debug('Preview fullscreen exit was rejected.', error);
    } finally {
      previewEnteredFullscreenRef.current = false;
      setIsPreviewFullscreen(false);
    }
  };

  const togglePreviewFullscreen = async () => {
    if (typeof document !== 'undefined' && document.fullscreenElement) {
      await exitPreviewFullscreen();
      return;
    }

    await requestPreviewFullscreen();
  };

  const handleOpenVisualizerForRecord = async (record: ObservationRecord | null) => {
    if (!record) {
      return;
    }

    const visualizerWindow = window.open('', '_blank');

    const stateData = await getStateData(record.stateUrl);
    if (stateData) {
      localStorage.setItem('state.ini', JSON.stringify(stateData));
    }

    const gridId = record?.gridId ?? record?.grid_id ?? selectedReportGridId;
    if (!gridId || !projectId || !reportId || !record?.id) {
      return;
    }

    const encodedGridId = encodeURIComponent(String(gridId));
    const encodedProjectId = encodeURIComponent(String(projectId));
    const encodedReportId = encodeURIComponent(String(reportId));
    const encodedObservationId = encodeURIComponent(String(record.id));
    const visualizerUrl = `/visualizer/?grid_id=${encodedGridId}&project_id=${encodedProjectId}&report_id=${encodedReportId}&observation_id=${encodedObservationId}`;

    if (visualizerWindow) {
      visualizerWindow.location.href = visualizerUrl;
      return;
    }

    window.open(visualizerUrl, '_blank');
  };

  const getRecords = async () => {
    if (!projectId || !reportId) {
      setRecords([]);
      setRecordsLoading(false);
      return;
    }

    try {
      setRecordsLoading(true);
      const data = await getProjectObservations(String(projectId), String(reportId));
      const recordsWithAdditionalData = await enrichRecordsWithAssets(data);
      setRecords(recordsWithAdditionalData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setRecordsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, [projectId, reportId]);

  useEffect(() => {
    const availableIds = new Set(records.map((record) => String(record.id)));
    setSelectedRecordIds((prevSelectedIds) => prevSelectedIds.filter((id) => availableIds.has(id)));
  }, [records]);

  const getPreviewOffset = (recordId: ObservationId) => {
    if (
      !dragState?.id ||
      !Array.isArray(visualOrder) ||
      visualOrder.length === 0 ||
      String(dragState.id) === String(recordId)
    ) {
      return null;
    }

    const originalIndex = dragStartOrderRef.current.indexOf(String(recordId));
    const previewIndex = visualOrder.indexOf(String(recordId));
    const originalPosition = dragSlotPositionsRef.current[originalIndex];
    const previewPosition = dragSlotPositionsRef.current[previewIndex];

    if (!originalPosition || !previewPosition) {
      return null;
    }

    return {
      x: previewPosition.left - originalPosition.left,
      y: previewPosition.top - originalPosition.top,
    };
  };

  const getDropTargetIndex = (draggedId: ObservationId) => {
    const pointerPosition = pointerPositionRef.current;
    const draggedNode = getRecordNodeRef(draggedId).current;
    const activeDraggedIdSet = new Set(
      activeDragRecordIdsRef.current.length ? activeDragRecordIdsRef.current : [String(draggedId)]
    );
    if (!pointerPosition && !draggedNode) {
      return null;
    }

    const draggedRect = draggedNode?.getBoundingClientRect();
    const draggedCenterX = pointerPosition?.x ?? (draggedRect ? draggedRect.left + draggedRect.width / 2 : 0);
    const draggedCenterY = pointerPosition?.y ?? (draggedRect ? draggedRect.top + draggedRect.height / 2 : 0);

    const candidates = reorderableRecords
      .filter((record) => !activeDraggedIdSet.has(String(record.id)))
      .map((record) => {
        const node = getRecordNodeRef(record.id).current;
        if (!node) {
          return null;
        }
        return {
          id: String(record.id),
          rect: node.getBoundingClientRect(),
        };
      })
      .filter((candidate): candidate is { id: string; rect: DOMRect } => candidate !== null)
      .sort((left, right) => {
        if (left.rect.top !== right.rect.top) {
          return left.rect.top - right.rect.top;
        }
        return left.rect.left - right.rect.left;
      });

    if (candidates.length === 0) {
      return 0;
    }

    for (let index = 0; index < candidates.length; index += 1) {
      const candidate = candidates[index];
      const candidateCenterX = candidate.rect.left + candidate.rect.width / 2;
      const candidateTop = candidate.rect.top;
      const candidateBottom = candidate.rect.bottom;
      const isBeforeRow = draggedCenterY < candidateTop;
      const isInsideRow = draggedCenterY >= candidateTop && draggedCenterY <= candidateBottom;

      if (isBeforeRow || (isInsideRow && draggedCenterX < candidateCenterX)) {
        return index;
      }
    }

    return candidates.length;
  };

  const updatePreviewOrder = (draggedId: ObservationId) => {
    const nextPreviewIndex = getDropTargetIndex(String(draggedId));

    if (nextPreviewIndex === null) {
      return;
    }

    setVisualOrder((prev) => {
      const currentOrderIds = getCurrentOrder(reorderableRecords, prev);
      const draggedIds = activeDragRecordIdsRef.current.length ? activeDragRecordIdsRef.current : [String(draggedId)];
      const nextOrderIds = reorderIdsGroup(currentOrderIds, draggedIds, nextPreviewIndex);
      return currentOrderIds.every((id, index) => id === nextOrderIds[index]) ? prev : nextOrderIds;
    });
  };

  const stopAutoScroll = () => {
    autoScrollStateRef.current.draggedId = null;
    if (autoScrollStateRef.current.frame !== null) {
      cancelAnimationFrame(autoScrollStateRef.current.frame);
      autoScrollStateRef.current.frame = null;
    }
  };

  const getAutoScrollDelta = (container: HTMLDivElement, pointerY: number) => {
    const containerRect = container.getBoundingClientRect();
    const distanceFromTop = pointerY - containerRect.top;
    const distanceFromBottom = containerRect.bottom - pointerY;

    if (distanceFromTop < AUTO_SCROLL_THRESHOLD_PX) {
      const intensity = Math.min(
        1,
        Math.max(0, (AUTO_SCROLL_THRESHOLD_PX - distanceFromTop) / AUTO_SCROLL_THRESHOLD_PX)
      );
      return -Math.max(8, Math.ceil(AUTO_SCROLL_MAX_SPEED_PX * intensity));
    }

    if (distanceFromBottom < AUTO_SCROLL_THRESHOLD_PX) {
      const intensity = Math.min(
        1,
        Math.max(0, (AUTO_SCROLL_THRESHOLD_PX - distanceFromBottom) / AUTO_SCROLL_THRESHOLD_PX)
      );
      return Math.max(8, Math.ceil(AUTO_SCROLL_MAX_SPEED_PX * intensity));
    }

    return 0;
  };

  const startAutoScroll = (draggedId: string) => {
    const container = pageContainerRef.current;
    if (!container) {
      stopAutoScroll();
      return;
    }

    autoScrollStateRef.current.draggedId = draggedId;

    if (autoScrollStateRef.current.frame !== null) {
      return;
    }

    const tick = () => {
      const node = pageContainerRef.current;
      const { draggedId: currentDraggedId } = autoScrollStateRef.current;
      const pointerPosition = pointerPositionRef.current;

      if (!node || !currentDraggedId || !pointerPosition) {
        autoScrollStateRef.current.frame = null;
        return;
      }

      const delta = getAutoScrollDelta(node, pointerPosition.y);
      if (delta === 0) {
        autoScrollStateRef.current.frame = null;
        return;
      }

      const previousScrollTop = node.scrollTop;
      node.scrollBy({ top: delta });
      const actualScrollDelta = node.scrollTop - previousScrollTop;

      if (actualScrollDelta === 0) {
        autoScrollStateRef.current.frame = null;
        return;
      }

      dragPositionRef.current = {
        x: dragPositionRef.current.x,
        y: dragPositionRef.current.y + actualScrollDelta,
      };
      setDragState((prev) =>
        prev
          ? {
              ...prev,
              x: dragPositionRef.current.x,
              y: dragPositionRef.current.y,
            }
          : prev
      );

      updatePreviewOrder(currentDraggedId);
      autoScrollStateRef.current.frame = requestAnimationFrame(tick);
    };

    autoScrollStateRef.current.frame = requestAnimationFrame(tick);
  };

  const handleAutoScroll = (event: DraggableEvent, draggedId: string) => {
    const container = pageContainerRef.current;
    if (!container) {
      stopAutoScroll();
      return;
    }

    const pointerX = getPointerClientX(event);
    const pointerY = getPointerClientY(event);
    if (pointerX === null || pointerY === null) {
      pointerPositionRef.current = null;
      stopAutoScroll();
      return;
    }

    pointerPositionRef.current = { x: pointerX, y: pointerY };

    if (getAutoScrollDelta(container, pointerY) === 0) {
      stopAutoScroll();
      return;
    }

    startAutoScroll(draggedId);
  };

  useEffect(() => stopAutoScroll, []);

  const handleDragStart =
    (recordId: ObservationId): DraggableEventHandler =>
    () => {
      if (!isCustomSort || isReordering || anchorEl) {
        return false;
      }
      const currentOrderIds = getCurrentOrder(reorderableRecords, visualOrder);
      const draggedRecordId = String(recordId);
      const isSelectedRecord = selectedRecordIds.includes(draggedRecordId);
      activeDragRecordIdsRef.current = isSelectedRecord
        ? currentOrderIds.filter((id) => selectedRecordIds.includes(id))
        : [draggedRecordId];
      dragStartOrderRef.current = currentOrderIds;
      dragSlotPositionsRef.current = currentOrderIds.map((id) => {
        const node = getRecordNodeRef(id).current;
        if (!node) {
          return null;
        }
        const rect = node.getBoundingClientRect();
        return {
          left: rect.left,
          top: rect.top,
        };
      });
      dragDistanceRef.current = 0;
      dragPositionRef.current = { x: 0, y: 0 };
      pointerPositionRef.current = null;
      suppressNextPreviewRef.current = false;
      setVisualOrder(currentOrderIds);
      setDragState({
        id: draggedRecordId,
        x: 0,
        y: 0,
      });
      handleClose();
      return;
    };

  const handleDrag = (recordId: ObservationId) => (_event: DraggableEvent, data: DraggableData) => {
    dragDistanceRef.current += Math.abs(data.deltaX) + Math.abs(data.deltaY);
    handleAutoScroll(_event, String(recordId));
    updatePreviewOrder(recordId);
    dragPositionRef.current = {
      x: data.x,
      y: data.y,
    };

    setDragState({
      id: String(recordId),
      x: data.x,
      y: data.y,
    });
  };

  const handleDragStop =
    (recordId: ObservationId): DraggableEventHandler =>
    () => {
      void (async () => {
        stopAutoScroll();
        dragPositionRef.current = { x: 0, y: 0 };
        pointerPositionRef.current = null;
        const didMove = dragDistanceRef.current >= DRAG_MIN_DISTANCE;
        const currentDragId = String(recordId);
        const currentOrderIds = getCurrentOrder(reorderableRecords, visualOrder);
        const targetIndex = didMove ? currentOrderIds.indexOf(currentDragId) : -1;

        dragDistanceRef.current = 0;

        if (!didMove) {
          setDragState(null);
          setVisualOrder(null);
          dragStartOrderRef.current = [];
          dragSlotPositionsRef.current = [];
          activeDragRecordIdsRef.current = [];
          return;
        }

        suppressNextPreviewRef.current = true;

        if (targetIndex === -1) {
          setDragState(null);
          setVisualOrder(null);
          dragStartOrderRef.current = [];
          dragSlotPositionsRef.current = [];
          activeDragRecordIdsRef.current = [];
          return;
        }

        const currentIds = dragStartOrderRef.current.length
          ? dragStartOrderRef.current
          : reorderableRecords.map((record) => String(record.id));
        const nextIds = currentOrderIds;

        if (currentIds.every((id, index) => id === nextIds[index])) {
          setDragState(null);
          setVisualOrder(null);
          dragStartOrderRef.current = [];
          dragSlotPositionsRef.current = [];
          activeDragRecordIdsRef.current = [];
          return;
        }

        const draggedIds = activeDragRecordIdsRef.current;
        const shouldClearSelection = draggedIds.some((id) => selectedRecordIds.includes(id));
        const previousRecords = records;
        const optimisticRecords = applyCustomOrderToRecords(records, nextIds);
        setIsDropCommitting(true);
        setRecords(optimisticRecords);
        setDragState(null);
        setVisualOrder(null);
        dragStartOrderRef.current = [];
        dragSlotPositionsRef.current = [];
        activeDragRecordIdsRef.current = [];
        if (shouldClearSelection) {
          setSelectedRecordIds([]);
        }
        setIsReordering(true);
        requestAnimationFrame(() => {
          setIsDropCommitting(false);
        });

        try {
          const reorderedRecords = await reorderProjectObservations(String(projectId), String(reportId), nextIds);
          const recordsWithAssets = await enrichRecordsWithAssets(reorderedRecords);
          setRecords([...recordsWithAssets, ...previousRecords.filter(isDeletedObservation)]);
        } catch (error) {
          console.error('Error reordering observations:', error);
          setRecords(previousRecords);
          const errorStatus =
            error && typeof error === 'object' && 'status' in error && typeof error.status === 'number'
              ? error.status
              : undefined;

          if (errorStatus === 400) {
            context.notification.show(
              'Could not reorder observations. Please refresh the observations with the refresh button and try again.',
              'error',
              true,
              'toast'
            );
          } else {
            context.notification.show('Could not reorder observations', 'error', true, 'toast');
          }
        } finally {
          setIsReordering(false);
        }
      })();
    };

  const handleClickThreeButton = (event: React.MouseEvent<HTMLElement>, pRecord: ObservationRecord) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(pRecord);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const handleReplaceImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const image = event.target.files?.[0];
    const record = pendingReplaceRecordRef.current;
    pendingReplaceRecordRef.current = null;
    event.target.value = '';

    if (!image || !record || isReplacingImage) {
      return;
    }

    try {
      setIsReplacingImage(true);
      await replaceProjectObservationImage(String(projectId), String(reportId), String(record.id), image);
      await getRecords();
      context.notification.show('Observation image was replaced', 'success', true, 'toast');
    } catch (error) {
      console.error('Error replacing observation image:', error);
      context.notification.show('Could not replace observation image', 'error', true, 'toast');
    } finally {
      setIsReplacingImage(false);
    }
  };

  const handleReplaceImageClick = () => {
    pendingReplaceRecordRef.current = selectedRecord;
    replaceImageInputRef.current?.click();
    setAnchorEl(null);
  };
  // 3 point for actions end

  const handleDeleteRecord = (pRecord: ObservationRecord | null) => {
    if (!pRecord) {
      return;
    }
    context.modal.show(
      {
        title: `Delete ${pRecord.title}`,
        form: {},
        buttonText: 'Delete',
        text: `Are you sure you want to delete ${pRecord.title}?`,
        customSubmit: () => deleteProjectObservation(String(projectId), String(reportId), String(pRecord.id)),
        destructive: true,
      },
      () => {
        setRecords((prevRecords) => prevRecords.filter((record) => String(record.id) !== String(pRecord.id)));
        delete recordNodeRefs.current[pRecord.id];
        if (String(selectedRecord?.id) === String(pRecord.id)) {
          setSelectedRecord(null);
        }
        context.notification.show(`${pRecord.title} was deleted`, 'success', true, 'toast');
      }
    );
  };

  // update start
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const handleOpenUpdateModal = () => setOpenUpdateModal(true);
  const handleCloseUpdateModal = () => setOpenUpdateModal(false);

  const handleInputsChange = (name: string, value: string) => {
    setWillBeUpdatedRecord((prevState) => ({
      ...(prevState ?? ({} as ObservationRecord)),
      [name]: value,
    }));
  };

  const handleUpdateRecord = async () => {
    await updateProjectObservation(String(projectId), String(reportId), String(willBeUpdatedRecord?.id), {
      title: willBeUpdatedRecord?.title,
      description: willBeUpdatedRecord?.description,
    });

    handleCloseUpdateModal();
    await getRecords();
    context.notification.show(`Record was updated`, 'success', true, 'toast');
  };
  // update end

  const updateRecordLocally = (recordId: ObservationId, updates: Partial<ObservationRecord>) => {
    setRecords((prevRecords) =>
      prevRecords.map((record) => (String(record.id) === String(recordId) ? { ...record, ...updates } : record))
    );

    setSelectedRecord((prevRecord) =>
      prevRecord && String(prevRecord.id) === String(recordId) ? { ...prevRecord, ...updates } : prevRecord
    );

    setWillBeUpdatedRecord((prevRecord) =>
      prevRecord && String(prevRecord.id) === String(recordId) ? { ...prevRecord, ...updates } : prevRecord
    );
  };

  // copy start
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const handleOpenCopyModal = () => setOpenCopyModal(true);
  const handleCloseCopyModal = () => {
    setOpenCopyModal(false);
    setWillBeCopiedToReport('');
  };

  const [willBeCopiedToReport, setWillBeCopiedToReport] = useState('');
  const canCopyRecord = Boolean(willBeCopiedToReport);

  const handleCopyRecord = async () => {
    if (!canCopyRecord) {
      return;
    }

    await copyProjectObservation(
      String(projectId),
      String(reportId),
      String(selectedRecord?.id),
      String(willBeCopiedToReport)
    );

    handleCloseCopyModal();
    await getRecords();
    handleClose();
    context.notification.show(`Record was copied`, 'success', true, 'toast');
  };
  // copy end

  // preview start
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const handleOpenPreviewModal = () => setOpenPreviewModal(true);
  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);
    setPreviewImageMaxHeight(null);
    setPreviewImageInsets(null);
    void exitPreviewFullscreen();
  };
  // preview end

  const handleCardClick = (record: ObservationRecord | null) => {
    setSelectedRecordIds((prevSelectedIds) => {
      if (!record) {
        return [];
      }
      return prevSelectedIds.includes(String(record.id)) && prevSelectedIds.length === 1 ? prevSelectedIds : [];
    });
    if (suppressNextPreviewRef.current) {
      suppressNextPreviewRef.current = false;
      return;
    }
    if (anchorEl) {
      return;
    }
    handlePreviewRecord(record);
  };

  const handleRecordSelectionClick = (event: React.MouseEvent<HTMLElement>, record: ObservationRecord) => {
    if (isDeletedObservation(record) || !isCustomSort || !isMultiSelectModifierPressed(event)) {
      handleCardClick(record);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setSelectedRecordIds((prevSelectedIds) => {
      const recordId = String(record.id);
      if (prevSelectedIds.includes(recordId)) {
        return prevSelectedIds.filter((id) => id !== recordId);
      }
      return [...prevSelectedIds, recordId];
    });
  };

  const selectedPreviewIndex = sortedRecords.findIndex((record) => String(record.id) === String(selectedRecord?.id));
  const previousPreviewRecord = selectedPreviewIndex > 0 ? sortedRecords[selectedPreviewIndex - 1] : null;
  const nextPreviewRecord =
    selectedPreviewIndex >= 0 && selectedPreviewIndex < sortedRecords.length - 1
      ? sortedRecords[selectedPreviewIndex + 1]
      : null;

  const handlePreviewNavigation = (record: ObservationRecord | null) => {
    if (!record) {
      return;
    }
    setSelectedRecord(record);
  };

  const updatePreviewImageLayout = () => {
    const modalNode = previewModalRef.current;
    const imageWrapNode = previewImageWrapRef.current;
    const imageNode = previewImageRef.current;

    if (!modalNode || !imageWrapNode) {
      return;
    }

    const modalStyles = window.getComputedStyle(modalNode);
    const bottomPadding = Number.parseFloat(modalStyles.paddingBottom || '0') || 0;
    const actionsHeight = previewActionsRef.current?.offsetHeight ?? 0;
    const availableHeight = modalNode.clientHeight - bottomPadding - imageWrapNode.offsetTop - actionsHeight;

    const nextImageMaxHeight = Math.max(0, Math.floor(availableHeight));
    setPreviewImageMaxHeight(nextImageMaxHeight);

    if (imageNode && imageNode.naturalWidth > 0 && imageNode.naturalHeight > 0) {
      const wrapRect = imageWrapNode.getBoundingClientRect();
      const wrapWidth = wrapRect.width;
      const wrapHeight = Math.min(wrapRect.height, nextImageMaxHeight || wrapRect.height);
      const widthScale = wrapWidth / imageNode.naturalWidth;
      const heightScale = wrapHeight / imageNode.naturalHeight;
      const scale = Math.min(widthScale, heightScale);
      const renderedWidth = imageNode.naturalWidth * scale;
      const horizontalInsetInsideWrap = Math.max(0, (wrapWidth - renderedWidth) / 2);

      setPreviewImageInsets({
        left: Math.max(0, Math.floor(horizontalInsetInsideWrap)),
        right: Math.max(0, Math.floor(horizontalInsetInsideWrap)),
      });
    } else {
      setPreviewImageInsets(null);
    }
  };

  useEffect(() => {
    if (!openPreviewModal) {
      return;
    }

    const handlePreviewKeydown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && previousPreviewRecord) {
        event.preventDefault();
        handlePreviewNavigation(previousPreviewRecord);
      }

      if (event.key === 'ArrowRight' && nextPreviewRecord) {
        event.preventDefault();
        handlePreviewNavigation(nextPreviewRecord);
      }
    };

    window.addEventListener('keydown', handlePreviewKeydown);

    return () => {
      window.removeEventListener('keydown', handlePreviewKeydown);
    };
  }, [openPreviewModal, previousPreviewRecord, nextPreviewRecord]);

  useEffect(() => {
    if (!openPreviewModal) {
      return;
    }

    const frameId = window.requestAnimationFrame(updatePreviewImageLayout);
    window.addEventListener('resize', updatePreviewImageLayout);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updatePreviewImageLayout);
    };
  }, [openPreviewModal, selectedRecord?.id, selectedRecordDescription]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const handleFullscreenChange = () => {
      const isFullscreenActive = Boolean(document.fullscreenElement);
      previewEnteredFullscreenRef.current = isFullscreenActive;
      setIsPreviewFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (previewEnteredFullscreenRef.current && document.fullscreenElement) {
        void document.exitFullscreen();
      }
    };
  }, []);

  return (
    <div id="observation-records-page" ref={pageContainerRef}>
      <div className="header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BackArrow onClick={() => navigate(`/projects/${projectId}`)} aria-label="Back to reports" />
            <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
              {selectedReport?.name}
            </Typography>
            <RefreshIcon onClick={getRecords} />
          </Box>
        </Box>
      </div>

      {/* Sorting Dropdown */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-label">Sort By</InputLabel>
            <Select labelId="sort-label" value={sortBy} onChange={handleSortChange} label="Sort By">
              <MenuItem key="sort-custom" value="custom">
                Custom
              </MenuItem>
              <MenuItem key="sort-date" value="date">
                Creation Date
              </MenuItem>
            </Select>
          </FormControl>
          {isCustomSort ? (
            <Typography variant="caption" sx={{ fontSize: '0.9rem !important', mb: '2px' }}>
              Drag and drop tiles to set a custom order. Use {multiSelectModifierLabel} + click to select multiple
              tiles.
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Grid container spacing={2}>
        {recordsLoading ? (
          <Grid size={12}>
            <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
              Loading records...
            </Alert>
          </Grid>
        ) : sortedRecords.length === 0 ? (
          <Grid size={12}>
            <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
              No records found!
            </Alert>
          </Grid>
        ) : (
          sortedRecords.map((record) => {
            const nodeRef = getRecordNodeRef(record.id);
            const recordId = String(record.id);
            const isDeleted = isDeletedObservation(record);
            const isActiveDragRecord = activeDragRecordIdsRef.current.includes(recordId);
            const isDragging = Boolean(dragState && isActiveDragRecord);
            const isSelectedForMultiDrag = selectedRecordIds.includes(recordId);
            const previewOffset = isDeleted ? null : getPreviewOffset(record.id);
            return (
              <Grid size={3} key={record.id} className="observation-record-grid-item">
                <Draggable
                  nodeRef={nodeRef}
                  cancel=".observation-record-title, .observation-record-menu-trigger"
                  disabled={isDeleted || !isCustomSort || isReordering || Boolean(anchorEl)}
                  position={
                    isDragging && dragState ? { x: dragState.x, y: dragState.y } : (previewOffset ?? { x: 0, y: 0 })
                  }
                  onStart={handleDragStart(record.id)}
                  onDrag={handleDrag(record.id)}
                  onStop={handleDragStop(record.id)}
                >
                  <div
                    ref={nodeRef}
                    className={`observation-record-tile ${isDeleted ? 'observation-record-tile--deleted' : ''} ${isDragging ? 'observation-record-tile--dragging' : ''} ${previewOffset ? 'observation-record-tile--previewing' : ''} ${isDropCommitting ? 'observation-record-tile--drop-committing' : ''} ${isSelectedForMultiDrag ? 'observation-record-tile--selected' : ''}`}
                    data-record-id={record.id}
                  >
                    <Card
                      className={`stat observation-record-tile__card ${isCustomSort && !isDeleted ? 'observation-record-tile__card--draggable' : ''} ${isSelectedForMultiDrag ? 'observation-record-tile__card--selected' : ''}`}
                      onClick={(event) => handleRecordSelectionClick(event, record)}
                      style={{
                        cursor: isCustomSort && !isDeleted ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
                      }}
                    >
                      <CardContent sx={{ paddingBottom: '5px !important' }}>
                        <div className="observation-record-tile__thumbnail">
                          <img
                            src={record?.thumbnailUrl}
                            alt="observation record"
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                          />
                        </div>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div
                            className="observation-record-title"
                            style={{ minWidth: 0, flex: 1, cursor: 'text' }}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            onDoubleClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {isDeleted ? (
                              <Typography onClick={() => handlePreviewRecord(record)} noWrap>
                                {record.title || 'Untitled record'} (deleted)
                              </Typography>
                            ) : (
                              <EditableLabel
                                label={record?.title ?? ''}
                                item={record}
                                keyToEdit="title"
                                onEdit={(value) => updateRecordLocally(record.id, { title: value })}
                                onSave={(value) =>
                                  updateProjectObservation(String(projectId), String(reportId), String(record.id), {
                                    title: value,
                                  })
                                }
                                onSingleClick={() => handlePreviewRecord(record)}
                                useTruncation
                                placeholder="Untitled record"
                              />
                            )}
                          </div>
                          <IconButton
                            className="observation-record-menu-trigger"
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClickThreeButton(event, record);
                            }}
                          >
                            <VerticalDotsIcon />
                          </IconButton>
                        </Box>
                      </CardContent>

                      <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={(_event, _reason) => {
                          handleClose();
                        }}
                        className="actions-menu"
                      >
                        <MenuItem
                          className="actions-menu-item"
                          onClick={() => {
                            handleOpenVisualizerForRecord(selectedRecord);
                            handleClose();
                          }}
                        >
                          <Tooltip title={`Open ${selectedRecord?.title} in 3D Visualizer`}>
                            <IconButton aria-label="open visualizer">
                              <div className="open-visualizer-btn open-visualizer-btn--small">
                                <span>3D</span>
                              </div>
                            </IconButton>
                          </Tooltip>
                        </MenuItem>

                        <MenuItem
                          className="actions-menu-item"
                          disabled={isReplacingImage || Boolean(selectedRecord?.deletedAt)}
                          onClick={handleReplaceImageClick}
                        >
                          <Tooltip title={`Replace image for ${selectedRecord?.title}`}>
                            <IconButton aria-label="replace image" disabled={isReplacingImage}>
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>

                        <MenuItem
                          className="actions-menu-item"
                          onClick={() => {
                            handlePreviewRecord(selectedRecord);
                            setAnchorEl(null);
                          }}
                        >
                          <Tooltip title={`Expand ${selectedRecord?.title}`}>
                            <IconButton aria-label="preview">
                              <ExpandIcon />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>

                        <MenuItem
                          className="actions-menu-item"
                          disabled={Boolean(selectedRecord?.deletedAt)}
                          onClick={() => {
                            handleOpenUpdateModal();
                            setWillBeUpdatedRecord(selectedRecord);
                            handleClose();
                          }}
                        >
                          <Tooltip title={`Edit ${selectedRecord?.title}`}>
                            <IconButton aria-label="edit">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>

                        <MenuItem
                          className="actions-menu-item"
                          disabled={Boolean(selectedRecord?.deletedAt)}
                          onClick={() => {
                            handleOpenCopyModal();
                            setAnchorEl(null);
                          }}
                        >
                          <Tooltip title={`Copy ${selectedRecord?.title}`}>
                            <IconButton aria-label="copy">
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>

                        <MenuItem
                          className="actions-menu-item"
                          disabled={Boolean(selectedRecord?.deletedAt)}
                          onClick={() => {
                            handleDeleteRecord(selectedRecord);
                            handleClose();
                          }}
                        >
                          <Tooltip title={`Delete ${selectedRecord?.title}`}>
                            <IconButton aria-label="delete" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>
                      </Menu>
                    </Card>
                  </div>
                </Draggable>
              </Grid>
            );
          })
        )}
      </Grid>

      <input ref={replaceImageInputRef} type="file" accept="image/*" hidden onChange={handleReplaceImageSelection} />

      {/* Update Modal */}
      <Modal
        open={openUpdateModal}
        onClose={handleCloseUpdateModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="custom-modal"
      >
        <Box className="observation-record-modal observation-record-update-modal observation-record-edit-modal">
          <br />
          <TextInput
            label="Title"
            name="title"
            value={willBeUpdatedRecord?.title}
            onChange={handleInputsChange}
            width="100%"
            inputStyle={{ backgroundColor: 'var(--theme-bg-color)' }}
          />
          <TextInput
            label="Description"
            name="description"
            value={willBeUpdatedRecord?.description ?? undefined}
            onChange={handleInputsChange}
            width="100%"
            type="textarea"
            rows={5}
            inputStyle={{ backgroundColor: 'var(--theme-bg-color)' }}
          />
          <br />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button variant="contained" className="modal-no-btn" onClick={handleCloseUpdateModal}>
              Cancel
            </Button>
            <Button variant="contained" className="modal-yes-btn" onClick={handleUpdateRecord}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Move Modal */}
      <Modal
        open={openCopyModal}
        onClose={handleCloseCopyModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="custom-modal"
      >
        <Box className="observation-record-modal">
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Copy Observation
          </Typography>
          <br />
          <FormControl variant="standard" fullWidth>
            <InputLabel>Select a report</InputLabel>
            <Select value={willBeCopiedToReport} onChange={(e) => setWillBeCopiedToReport(e.target.value)}>
              {reports?.map((report) => (
                <MenuItem key={`copy-report-${report.id || report.name}`} value={report.id}>
                  {report.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <br />
          <br />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button
              variant="contained"
              className="modal-no-btn"
              onClick={() => {
                handleCloseCopyModal();
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" className="modal-yes-btn" onClick={handleCopyRecord} disabled={!canCopyRecord}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={openPreviewModal}
        onClose={handleClosePreviewModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="custom-modal"
      >
        <Box ref={previewModalRef} className="observation-record-modal observation-record-preview-modal">
          <Typography className="observation-record-preview-modal__title" component="div">
            {selectedRecord ? (
              <EditableLabel
                label={selectedRecord.title ?? ''}
                item={selectedRecord}
                keyToEdit="title"
                onEdit={(value) => updateRecordLocally(selectedRecord.id, { title: value })}
                onSave={(value) =>
                  updateProjectObservation(String(projectId), String(reportId), String(selectedRecord.id), {
                    title: value,
                  })
                }
                placeholder="Untitled record"
              />
            ) : null}
          </Typography>
          {previousPreviewRecord ? (
            <IconButton
              className="observation-record-preview-modal__nav observation-record-preview-modal__nav--left"
              onClick={() => handlePreviewNavigation(previousPreviewRecord)}
            >
              <LeftDropDownArrow size={18} />
            </IconButton>
          ) : null}
          {nextPreviewRecord ? (
            <IconButton
              className="observation-record-preview-modal__nav observation-record-preview-modal__nav--right"
              onClick={() => handlePreviewNavigation(nextPreviewRecord)}
            >
              <RightDropDownArrow size={18} />
            </IconButton>
          ) : null}
          <Box ref={previewImageWrapRef} className="observation-record-preview-modal__image-wrap">
            <img
              ref={previewImageRef}
              src={selectedRecord?.imageUrl}
              alt={selectedRecord?.title}
              style={previewImageMaxHeight ? { maxHeight: `${previewImageMaxHeight}px` } : undefined}
              onLoad={updatePreviewImageLayout}
            />
          </Box>
          <Box
            ref={previewActionsRef}
            className="observation-record-preview-modal__actions"
            style={
              previewImageInsets
                ? {
                    paddingLeft: `${previewImageInsets.left}px`,
                    paddingRight: `${previewImageInsets.right}px`,
                  }
                : undefined
            }
          >
            {selectedRecordDescription ? (
              <Box className="observation-record-preview-modal__description">
                <Typography className="observation-record-preview-modal__description-label" component="span">
                  Description:
                </Typography>
                <Typography className="observation-record-preview-modal__description-text" component="div">
                  <Box className="observation-record-preview-modal__description-editor">
                    {selectedRecord ? (
                      <EditableLabel
                        label={selectedRecordDescription}
                        item={selectedRecord}
                        keyToEdit="description"
                        onEdit={(value) => updateRecordLocally(selectedRecord.id, { description: value })}
                        onSave={(value) =>
                          updateProjectObservation(String(projectId), String(reportId), String(selectedRecord.id), {
                            description: value,
                          })
                        }
                        useTruncation
                        placeholder=""
                      />
                    ) : null}
                  </Box>
                </Typography>
              </Box>
            ) : null}
            <Box className="observation-record-preview-modal__action-buttons">
              <Tooltip title={`Open ${selectedRecord?.title} in 3D Visualizer`}>
                <IconButton aria-label="open visualizer" onClick={() => handleOpenVisualizerForRecord(selectedRecord)}>
                  <div className="open-visualizer-btn">
                    <span>3D</span>
                  </div>
                </IconButton>
              </Tooltip>
              <Tooltip title={isPreviewFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                <IconButton aria-label="toggle fullscreen" onClick={() => void togglePreviewFullscreen()}>
                  <div
                    className={`observation-record-preview-modal__fullscreen-btn ${isPreviewFullscreen ? 'observation-record-preview-modal__fullscreen-btn--active' : ''}`}
                  >
                    <span>
                      <FullScreenIcon />
                    </span>
                  </div>
                </IconButton>
              </Tooltip>
              <Button variant="contained" className="modal-no-btn" onClick={handleClosePreviewModal}>
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Records;
